import clsx from 'clsx'
import { motion } from 'motion/react'
import React, {
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { type Money } from 'utils/web_api.schemas'

import YoYChangeIndicator from '../YoYChangeIndicator/YoYChangeIndicator'

export interface BudgetComparisonBarProps {
  condensed: boolean;
  prevPlanYear: string;
  currentPlanYear: string;
  prevAmount: Money;
  currentAmount: Money | null;
  yoyChange: Money | null;
  xAxisMaxAmountSubunits: number;
  increaseIsGreen: boolean;
  prevLabel?: string;
}

interface BarProps {
  planYear: string;
  isCurrentYear: boolean;
  amount: Money;
  maxAmountSubunits: number;
  percentageChange?: number | null;
  increaseIsGreen?: boolean;
  condensed?: boolean;
  amountIsUnknown?: boolean;
}
interface BudgetComparisonBarWithDifferentSignsProps {
  condensed: boolean;
  prevPlanYear: string;
  currentPlanYear: string;
  prevAmount: Money;
  currentAmount: Money;
  xAxisMaxAmountSubunits: number;
  prevLabel?: string;
  yoyChangeSubunits: number;
}

export const Bar = ({
  planYear,
  isCurrentYear,
  amount,
  maxAmountSubunits,
  percentageChange,
  increaseIsGreen,
  condensed,
  amountIsUnknown,
}: BarProps) => {
  // Memoize width percentage calculation
  const widthPercentage = useMemo(() => {
    if (maxAmountSubunits === 0) return 0
    return Math.abs(amount.subunits / maxAmountSubunits)
  }, [amount.subunits, maxAmountSubunits])

  const outerContainerRef = useRef<HTMLDivElement>(null)
  const fullWidthRef = useRef<HTMLDivElement>(null)
  const labelWidthRef = useRef<HTMLDivElement>(null)
  const planYearWidthRef = useRef<HTMLSpanElement>(null)
  const [barWidth, setBarWidth] = useState(0)

  const calculateBarWidth = useCallback(() => {
    if (outerContainerRef.current && labelWidthRef.current) {
      const availableWidth =
        outerContainerRef.current.offsetWidth -
        labelWidthRef.current.offsetWidth -
        (planYearWidthRef.current?.offsetWidth || 0) -
        16
      const barWidth = Math.max(0, availableWidth * widthPercentage)
      setBarWidth(barWidth)
    }
  }, [widthPercentage])

  useLayoutEffect(() => {
    calculateBarWidth()

    const resizeObserver = new ResizeObserver(calculateBarWidth)
    if (outerContainerRef.current) {
      resizeObserver.observe(outerContainerRef.current)
    }
    if (planYearWidthRef.current) {
      resizeObserver.observe(planYearWidthRef.current)
    }
    if (fullWidthRef.current) {
      resizeObserver.observe(fullWidthRef.current)
    }
    if (labelWidthRef.current) {
      resizeObserver.observe(labelWidthRef.current)
    }

    // Throttle window resize for better performance
    let resizeTimeout: ReturnType<typeof setTimeout>
    const throttledResize = () => {
      clearTimeout(resizeTimeout)
      resizeTimeout = setTimeout(calculateBarWidth, 16) // ~60fps
    }

    window.addEventListener('resize', throttledResize)

    return () => {
      resizeObserver.disconnect()
      window.removeEventListener('resize', throttledResize)
      clearTimeout(resizeTimeout)
    }
  }, [widthPercentage, condensed])

  return (
    <div
      ref={outerContainerRef}
      className='flex flex-row gap-2 items-center w-full'
    >
      {!condensed && (
        <span ref={planYearWidthRef} className='body text-gray-800 w-fit'>
          {planYear}
        </span>
      )}
      <div
        className='flex flex-row gap-2 items-center flex-grow'
        ref={fullWidthRef}
      >
        <motion.div
          className={clsx(
            'rounded-full',
            isCurrentYear ? 'bg-thatch-400' : 'bg-gray-300',
            { 'h-3': condensed, 'h-4': !condensed },
            amountIsUnknown
              ? 'bg-white border border-gray-400 border-dashed'
              : ''
          )}
          style={{ width: barWidth }}
          layout='size'
        />
        <div className='flex flex-row gap-2 items-center' ref={labelWidthRef}>
          <span
            className={clsx({
              'body text-gray-500': !isCurrentYear,
              'body-bold text-gray-800': isCurrentYear,
              'body-sm': condensed && !isCurrentYear,
              'body-sm-bold': condensed && isCurrentYear,
            })}
          >
            {amountIsUnknown
              ? 'Unknown'
              : amount.formatted_amount.replace('-', '')}
          </span>
          {!condensed &&
            !!percentageChange &&
            percentageChange !== 0 &&
            !amountIsUnknown && (
              <YoYChangeIndicator
                change={percentageChange}
                displayValue={`${Math.abs(percentageChange)}%`}
                increaseIsGreen={increaseIsGreen || false}
                makeInvisible={!isCurrentYear}
              />
          )}
        </div>
      </div>
    </div>
  )
}

const BudgetComparisonBarWithDifferentSigns: React.FC<
  BudgetComparisonBarWithDifferentSignsProps
> = ({
  condensed,
  prevPlanYear,
  currentPlanYear,
  prevAmount,
  currentAmount,
  xAxisMaxAmountSubunits,
  prevLabel,
  yoyChangeSubunits,
}) => {
  const maxAmount = xAxisMaxAmountSubunits + Math.abs(prevAmount.subunits)
  const leftWidth = Math.abs(prevAmount.subunits / maxAmount) * 100
  const rightWidth = Math.abs(currentAmount.subunits / maxAmount) * 100

  return (
    <table
      data-testid='budget-comparison-bar-with-different-signs'
      className='table-fixed'
    >
      <tbody>
        <tr>
          {!condensed && (
            <td
              className='body text-gray-800 py-1 pr-1 whitespace-nowrap'
              style={{ width: '1%' }}
            >
              {prevPlanYear}
            </td>
          )}
          <td
            className='border-r border-gray-200 border-dashed px-1'
            width={`${leftWidth}%`}
          >
            <div
              className={clsx(
                'rounded-full flex-grow w-full bg-gray-300',
                { 'h-3': condensed, 'h-4': !condensed }
              )}
            />
          </td>
          <td className='px-1' colSpan={2}>
            <div className='flex flex-row gap-2 items-center'>
              <span
                className={clsx('text-gray-500', {
                  body: !condensed,
                  'body-sm': condensed,
                })}
              >
                {prevAmount.formatted_amount}
                {!condensed && prevLabel && (
                  <span className='caption text-gray-500 ml-1 text-nowrap'>
                    {prevLabel}
                  </span>
                )}
              </span>
            </div>
          </td>
        </tr>
        <tr>
          {!condensed && (
            <td
              className='body text-gray-800 py-1 pr-1 whitespace-nowrap'
              style={{ width: '1%' }}
            >
              {currentPlanYear}
            </td>
          )}
          <td className='border-r border-gray-200 border-dashed px-1' />
          <td className='px-1' width={`${rightWidth}%`}>
            <div
              className={clsx('rounded-full flex-grow w-full bg-thatch-400', {
                'h-3': condensed,
                'h-4': !condensed,
              })}
            />
          </td>
          <td className='px-1'>
            <div className='flex flex-row gap-2 items-center'>
              <span
                className={clsx('text-gray-800', {
                  'body-bold': !condensed,
                  'body-sm-bold': condensed,
                })}
              >
                {currentAmount.formatted_amount}
              </span>
              {!condensed && (
                <YoYChangeIndicator
                  change={yoyChangeSubunits}
                  displayValue=''
                  increaseIsGreen
                  makeInvisible={false}
                />
              )}
            </div>
          </td>
        </tr>
      </tbody>
    </table>
  )
}

const BudgetComparisonBar: React.FC<BudgetComparisonBarProps> = ({
  condensed,
  prevPlanYear,
  currentPlanYear,
  prevAmount,
  currentAmount,
  yoyChange,
  xAxisMaxAmountSubunits,
  increaseIsGreen,
  prevLabel,
}) => {
  if (
    currentAmount &&
    !!yoyChange &&
    prevAmount.subunits * currentAmount.subunits < 0
  ) {
    return (
      <BudgetComparisonBarWithDifferentSigns
        condensed={condensed}
        prevPlanYear={prevPlanYear}
        currentPlanYear={currentPlanYear}
        prevAmount={prevAmount}
        currentAmount={currentAmount}
        xAxisMaxAmountSubunits={xAxisMaxAmountSubunits}
        prevLabel={prevLabel}
        yoyChangeSubunits={yoyChange.subunits}
      />
    )
  }

  // Memoize percentage calculation
  const percentageChange = useMemo(() => {
    if (condensed || !yoyChange) return null

    if (prevAmount.subunits === 0 || yoyChange.subunits === 0) {
      return 0
    }

    return Math.round((yoyChange.subunits / prevAmount.subunits) * 100)
  }, [condensed, prevAmount.subunits, yoyChange?.subunits])

  return (
    <div
      data-testid='budget-comparison-bar'
      className={clsx('flex flex-col', {
        'gap-2': !condensed,
        'gap-1': condensed,
      })}
    >
      <Bar
        planYear={prevPlanYear}
        isCurrentYear={false}
        amount={prevAmount}
        maxAmountSubunits={xAxisMaxAmountSubunits}
        condensed={condensed}
        increaseIsGreen={increaseIsGreen}
        percentageChange={percentageChange}
      />
      <Bar
        planYear={currentPlanYear}
        isCurrentYear
        amount={currentAmount || prevAmount}
        maxAmountSubunits={xAxisMaxAmountSubunits}
        condensed={condensed}
        increaseIsGreen={increaseIsGreen}
        percentageChange={percentageChange}
        amountIsUnknown={!currentAmount}
      />
    </div>
  )
}

export default BudgetComparisonBar
